import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import type { QueryMeta } from '@tanstack/react-query';
import { getApiErrorContext } from '@/helpers/sentry';

const sessionEnds = new WeakSet<AxiosError>();

// A 401 the auth layer answers by logging the user out is an expired session,
// not a bug.
export const markSessionEnd = (error: AxiosError) => {
  sessionEnds.add(error);
};

// Report Axios errors with status >= 400, including the response (for request id)
export const reportApiError = (
  error: unknown,
  query?: { meta?: QueryMeta },
) => {
  if (!(error instanceof AxiosError) || !error.response) return;

  const { response } = error;
  if (response.status < 400) return;
  if (sessionEnds.has(error)) return;
  if (query?.meta?.expectedErrorStatuses?.includes(response.status)) return;

  const { title, fingerprint, tags } = getApiErrorContext(error);

  Sentry.withScope((scope) => {
    scope.setFingerprint(fingerprint);
    scope.setTags(tags);
    scope.setExtras({
      response: response.data,
      request_id: response.headers['x-request-id'],
    });
    // Every Axios error shares the same message and stack, so give the issue
    // a title that names the failing request.
    scope.addEventProcessor((event) => {
      const exception = event.exception?.values?.[0];
      if (exception) exception.value = title;
      return event;
    });
    Sentry.captureException(error);
  });
};
